import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  updateDoc, 
  doc, 
  deleteDoc,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  FaCheck, 
  FaTimes, 
  FaFlag, 
  FaTrash, 
  FaEye, 
  FaUserCircle,
  FaClock,
  FaExclamationTriangle,
  FaChartBar,
  FaFilter,
  FaSearch,
  FaImage,
  FaComment,
  FaHeart,
  FaShieldAlt
} from 'react-icons/fa';

const Moderation = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, reported, approved
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    reported: 0
  });

  useEffect(() => {
    loadPosts();
    loadStats();
  }, []);

  const loadPosts = () => {
    const q = query(
      collection(db, 'mur_supporters'),
      orderBy('dateCreation', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateCreation: doc.data().dateCreation?.toDate()
      }));
      setPosts(postsData);
      setLoading(false);
    });

    return unsubscribe;
  };

  const loadStats = async () => {
    try {
      const allPosts = await getDocs(collection(db, 'mur_supporters'));
      const postsData = allPosts.docs.map(doc => doc.data());
      
      setStats({
        total: postsData.length,
        pending: postsData.filter(p => !p.modere).length,
        approved: postsData.filter(p => p.modere).length,
        reported: postsData.filter(p => p.signale).length
      });
    } catch (error) {
      console.error('Erreur loadStats:', error);
    }
  };

  const approvePost = async (postId) => {
    try {
      await updateDoc(doc(db, 'mur_supporters', postId), {
        modere: true,
        signale: false
      });
    } catch (error) {
      console.error('Erreur approvePost:', error);
    }
  };

  const rejectPost = async (postId) => {
    try {
      await deleteDoc(doc(db, 'mur_supporters', postId));
    } catch (error) {
      console.error('Erreur rejectPost:', error);
    }
  };

  const unreportPost = async (postId) => {
    try {
      await updateDoc(doc(db, 'mur_supporters', postId), {
        signale: false
      });
    } catch (error) {
      console.error('Erreur unreportPost:', error);
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'À l\'instant';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `il y a ${diffMins}min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    return `il y a ${diffDays}j`;
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.texte?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.userNom?.toLowerCase().includes(searchTerm.toLowerCase());
    
    switch (filter) {
      case 'pending':
        return !post.modere && matchesSearch;
      case 'reported':
        return post.signale && matchesSearch;
      case 'approved':
        return post.modere && matchesSearch;
      default:
        return matchesSearch;
    }
  });

  const getStatusColor = (post) => {
    if (post.signale) return 'text-red-400';
    if (!post.modere) return 'text-[#FFE400]';
    return 'text-[#BDFF00]';
  };

  const getStatusIcon = (post) => {
    if (post.signale) return FaFlag;
    if (!post.modere) return FaClock;
    return FaCheck;
  };

  const getStatusText = (post) => {
    if (post.signale) return 'Signalé';
    if (!post.modere) return 'En attente';
    return 'Approuvé';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#001d5a] to-[#002766] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative group mb-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-3xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
          <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center border border-white/20 shadow-2xl">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-block p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full mb-6"
            >
              <FaShieldAlt className="text-3xl text-[#001B56]" />
            </motion.div>
            
            <h1 className="text-4xl font-extrabold text-white mb-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] bg-clip-text text-transparent">
              Modération EFAFC
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Gérez et modérez les publications du mur des supporters
            </p>
          </div>
        </motion.div>

        {/* Statistiques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFE400] to-[#f4d03f] rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-lg">
              <FaChartBar className="text-3xl text-[#FFE400] mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#FFE400] mb-1">{stats.total}</div>
              <div className="text-white/80 font-medium">Total</div>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFE400] to-[#f4d03f] rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-lg">
              <FaClock className="text-3xl text-[#FFE400] mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#FFE400] mb-1">{stats.pending}</div>
              <div className="text-white/80 font-medium">En attente</div>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#BDFF00] to-[#a4e600] rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-lg">
              <FaCheck className="text-3xl text-[#BDFF00] mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#BDFF00] mb-1">{stats.approved}</div>
              <div className="text-white/80 font-medium">Approuvés</div>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-lg">
              <FaFlag className="text-3xl text-red-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-red-400 mb-1">{stats.reported}</div>
              <div className="text-white/80 font-medium">Signalés</div>
            </div>
          </div>
        </motion.div>

        {/* Filtres et recherche */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="relative group mb-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#001B56] to-[#0d47a1] rounded-2xl blur opacity-20"></div>
          <div className="relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl p-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Filtres */}
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'Tous', icon: FaChartBar },
                  { key: 'pending', label: 'En attente', icon: FaClock },
                  { key: 'reported', label: 'Signalés', icon: FaFlag },
                  { key: 'approved', label: 'Approuvés', icon: FaCheck }
                ].map(({ key, label, icon: Icon }) => (
                  <motion.button
                    key={key}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setFilter(key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                      filter === key
                        ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] font-bold'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    <Icon className="text-sm" />
                    {label}
                  </motion.button>
                ))}
              </div>

              {/* Recherche */}
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-[#BDFF00] focus:border-[#BDFF00] text-white placeholder-white/60 backdrop-blur-sm min-w-[300px]"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Liste des posts */}
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full blur opacity-20"></div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-full p-6 inline-block border border-white/20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BDFF00]"></div>
                </div>
              </div>
              <p className="mt-6 text-white/80 text-lg">Chargement des publications...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full blur opacity-20"></div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-full p-8 inline-block border border-white/20">
                  <FaFilter className="text-6xl text-[#BDFF00]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Aucune publication trouvée</h3>
              <p className="text-white/70 text-lg">Aucune publication ne correspond aux critères sélectionnés.</p>
            </motion.div>
          ) : (
            <AnimatePresence>
              {filteredPosts.map((post, index) => {
                const StatusIcon = getStatusIcon(post);
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ delay: index * 0.05 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl blur opacity-50"></div>
                    <div className="relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl overflow-hidden">
                      <div className="p-6">
                        {/* Header du post */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#FFE400] to-[#BDFF00] flex items-center justify-center">
                              {post.userAvatar ? (
                                <img src={post.userAvatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <FaUserCircle className="text-xl text-[#001B56]" />
                              )}
                            </div>
                            <div>
                              <div className="font-bold text-white">{post.userNom}</div>
                              <div className="text-white/60 text-sm">{formatTimeAgo(post.dateCreation)}</div>
                            </div>
                          </div>

                          {/* Statut */}
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 ${getStatusColor(post)}`}>
                            <StatusIcon className="text-sm" />
                            <span className="text-sm font-medium">{getStatusText(post)}</span>
                          </div>
                        </div>

                        {/* Contenu */}
                        {post.texte && (
                          <div className="mb-4">
                            <p className="text-white/90 leading-relaxed">{post.texte}</p>
                          </div>
                        )}

                        {post.imageUrl && (
                          <div className="mb-4">
                            <img 
                              src={post.imageUrl} 
                              alt="Post image"
                              className="w-full max-h-64 object-cover rounded-xl border border-white/20 cursor-pointer"
                              onClick={() => window.open(post.imageUrl, '_blank')}
                            />
                          </div>
                        )}

                        {/* Statistiques du post */}
                        <div className="flex items-center gap-6 mb-4 text-white/60">
                          <div className="flex items-center gap-2">
                            <FaHeart className="text-red-400" />
                            <span>{post.likes?.length || 0}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaComment className="text-[#BDFF00]" />
                            <span>{post.commentaires?.length || 0}</span>
                          </div>
                          {post.imageUrl && (
                            <div className="flex items-center gap-2">
                              <FaImage className="text-[#FFE400]" />
                              <span>Image</span>
                            </div>
                          )}
                        </div>

                        {/* Actions de modération */}
                        <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                          {!post.modere && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => approvePost(post.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#BDFF00] to-[#a4e600] text-[#001B56] rounded-xl hover:shadow-lg transition-all duration-300 font-bold"
                            >
                              <FaCheck />
                              Approuver
                            </motion.button>
                          )}

                          {post.signale && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => unreportPost(post.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFE400] to-[#f4d03f] text-[#001B56] rounded-xl hover:shadow-lg transition-all duration-300 font-bold"
                            >
                              <FaEye />
                              Marquer comme vu
                            </motion.button>
                          )}

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedPost(post)}
                            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20"
                          >
                            <FaEye />
                            Détails
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => rejectPost(post.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:shadow-lg transition-all duration-300 font-bold"
                          >
                            <FaTrash />
                            Supprimer
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>

        {/* Modal de détails */}
        <AnimatePresence>
          {selectedPost && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedPost(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white">Détails de la publication</h3>
                    <button
                      onClick={() => setSelectedPost(null)}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      <FaTimes className="text-xl" />
                    </button>
                  </div>

                  {/* Contenu du post */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#FFE400] to-[#BDFF00] flex items-center justify-center">
                        {selectedPost.userAvatar ? (
                          <img src={selectedPost.userAvatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <FaUserCircle className="text-2xl text-[#001B56]" />
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg">{selectedPost.userNom}</div>
                        <div className="text-white/60">{formatTimeAgo(selectedPost.dateCreation)}</div>
                      </div>
                    </div>

                    {selectedPost.texte && (
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-white/90 leading-relaxed">{selectedPost.texte}</p>
                      </div>
                    )}

                    {selectedPost.imageUrl && (
                      <div>
                        <img 
                          src={selectedPost.imageUrl} 
                          alt="Post image"
                          className="w-full max-h-96 object-cover rounded-xl border border-white/20"
                        />
                      </div>
                    )}

                    {/* Commentaires */}
                    {selectedPost.commentaires && selectedPost.commentaires.length > 0 && (
                      <div>
                        <h4 className="text-lg font-bold text-white mb-4">Commentaires ({selectedPost.commentaires.length})</h4>
                        <div className="space-y-3 max-h-40 overflow-y-auto">
                          {selectedPost.commentaires.map((comment, index) => (
                            <div key={index} className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#FFE400] to-[#BDFF00] flex items-center justify-center flex-shrink-0">
                                {comment.userAvatar ? (
                                  <img src={comment.userAvatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  <FaUserCircle className="text-sm text-[#001B56]" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-[#BDFF00] text-sm">{comment.userNom}</div>
                                <div className="text-white/90 text-sm">{comment.texte}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Moderation; 